import { Api, ApiError, type PublicKeyCredentialCreationOptionsJSON } from "../api/client.js";
import type { AssertionOptions, Receipt } from "../api/types.js";
import type { ScamAnswer } from "./scam.js";

/**
 * The WebAuthn client sequence, exactly as the spike and the lab console proved
 * it on device (20 Sep):
 *
 *  1. The assertion options are fetched when the card is DRAWN, not when the
 *     button is tapped, so the tap handler has nothing to await first.
 *  2. `navigator.credentials.get()` is the FIRST statement of `signNow`, and
 *     `signNow` is the first statement of the tap handler after the synchronous
 *     in-flight guard. Safari drops the user gesture across an `await`; a
 *     handler that fetches first works on desktop Chrome and fails silently on
 *     the phone in the demo.
 *  3. `allowCredentials` is the BANK's list of this customer's registered passkeys
 *     (DCTA-86, QA ruling R-b), passed through with the ids decoded: a phone that
 *     holds two customers' passkeys offers only the signer's. A bank that sends no
 *     list keeps the discoverable request (`[]`). The list is UX; the gateway's
 *     owner check is the control.
 */

export function fromB64Url(value: string): Uint8Array {
  let padded = value.replace(/-/g, "+").replace(/_/g, "/");
  while (padded.length % 4 !== 0) padded += "=";
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function toB64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i] as number);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * The bank's descriptors as `get()` takes them. Anything that is not `{ id: string }` is dropped,
 * never guessed. With `hints: ["hybrid"]` ("Use another device") the transports go: a synced
 * passkey stored here as "internal" may be on the other phone.
 */
export function allowList(list: readonly unknown[] | undefined, hints?: readonly string[]): PublicKeyCredentialDescriptor[] {
  const hybrid = hints?.includes("hybrid") === true;
  const out: PublicKeyCredentialDescriptor[] = [];
  for (const d of list ?? []) {
    const c = d as { id?: unknown; transports?: unknown } | null;
    if (c === null || typeof c !== "object" || typeof c.id !== "string" || c.id === "") continue;
    let id: Uint8Array;
    try {
      id = fromB64Url(c.id);
    } catch {
      continue; // not base64url: dropped — a throw here would end the tap before get()
    }
    const transports = Array.isArray(c.transports) ? c.transports.filter((t): t is string => typeof t === "string") : undefined;
    out.push({
      id: id as unknown as BufferSource,
      type: "public-key",
      ...(hybrid || transports === undefined || transports.length === 0 ? {} : { transports: transports as AuthenticatorTransport[] }),
    });
  }
  return out;
}

export const SHEET_CANCELLED = "SHEET_CANCELLED";
export const NO_WEBAUTHN = "NO_WEBAUTHN";

export function webAuthnAvailable(): boolean {
  return typeof PublicKeyCredential !== "undefined" && navigator.credentials !== undefined;
}

/**
 * Called INSIDE the tap handler. Nothing may be awaited before it. `hints: ["hybrid"]`
 * asks the platform for a passkey on another device (the recovery sheet's "Use another
 * device", §C.11); a browser that does not know the hint ignores it.
 */
export function signNow(
  api: Pick<Api, "confirm">,
  bundleId: string,
  options: AssertionOptions,
  answers: readonly ScamAnswer[] = [],
  credentials: CredentialsContainer = navigator.credentials,
  hints?: readonly string[],
): Promise<Receipt> {
  const ceremony = credentials.get({
    publicKey: {
      challenge: fromB64Url(options.challenge) as unknown as BufferSource,
      rpId: options.rpId,
      allowCredentials: allowList(options.allowCredentials, hints),
      userVerification: options.userVerification,
      ...(options.timeout === undefined ? {} : { timeout: options.timeout }),
      ...(hints === undefined ? {} : { hints: [...hints] }),
    } as PublicKeyCredentialRequestOptions,
  });
  return ceremony.then((credential) => {
    if (credential === null) throw new ApiError(SHEET_CANCELLED, 0, {});
    const assertion = credential as PublicKeyCredential;
    const response = assertion.response as AuthenticatorAssertionResponse;
    return api.confirm(
      bundleId,
      {
        id: assertion.id,
        rawId: toB64Url(assertion.rawId),
        type: assertion.type,
        clientExtensionResults: assertion.getClientExtensionResults(),
        response: {
          clientDataJSON: toB64Url(response.clientDataJSON),
          authenticatorData: toB64Url(response.authenticatorData),
          signature: toB64Url(response.signature),
          ...(response.userHandle === null || response.userHandle === undefined
            ? {}
            : { userHandle: toB64Url(response.userHandle) }),
        },
      },
      answers,
    );
  });
}

/**
 * Enrolment (UC-A1), in two halves for the same reason as signing: the
 * registration options are fetched when the enrolment sheet OPENS, and
 * `navigator.credentials.create()` is the first statement of the "Create a
 * passkey" tap handler — nothing awaited before it.
 */
export function registrationOptions(api: Api): Promise<PublicKeyCredentialCreationOptionsJSON> {
  if (!webAuthnAvailable()) return Promise.reject(new ApiError(NO_WEBAUTHN, 0, {}));
  return api.registrationOptions();
}

/** Called INSIDE the tap handler. Nothing may be awaited before it. */
export function createPasskey(
  api: Pick<Api, "registrationVerify">,
  options: PublicKeyCredentialCreationOptionsJSON,
  credentials: CredentialsContainer = navigator.credentials,
): Promise<void> {
  const ceremony = credentials.create({
    publicKey: {
      challenge: fromB64Url(options.challenge) as unknown as BufferSource,
      rp: options.rp,
      user: {
        id: fromB64Url(options.user.id) as unknown as BufferSource,
        name: options.user.name,
        displayName: options.user.displayName,
      },
      pubKeyCredParams: [...options.pubKeyCredParams],
      ...(options.timeout === undefined ? {} : { timeout: options.timeout }),
      ...(options.attestation === undefined ? {} : { attestation: options.attestation }),
      ...(options.authenticatorSelection === undefined
        ? {}
        : { authenticatorSelection: options.authenticatorSelection }),
      excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
        id: fromB64Url(c.id) as unknown as BufferSource,
        type: "public-key" as const,
        ...(c.transports === undefined
          ? {}
          : { transports: c.transports as AuthenticatorTransport[] }),
      })),
    },
  });
  return ceremony.then(async (created) => {
    if (created === null) throw new ApiError(SHEET_CANCELLED, 0, {});
    const credential = created as PublicKeyCredential;
    const response = credential.response as AuthenticatorAttestationResponse;
    await api.registrationVerify({
      id: credential.id,
      rawId: toB64Url(credential.rawId),
      type: credential.type,
      clientExtensionResults: credential.getClientExtensionResults(),
      response: {
        clientDataJSON: toB64Url(response.clientDataJSON),
        attestationObject: toB64Url(response.attestationObject),
        ...(typeof response.getTransports === "function"
          ? { transports: response.getTransports() }
          : {}),
      },
    });
  });
}

/** A platform that cannot hold a passkey says so with NotSupportedError / SecurityError. */
export function isUnsupported(error: unknown): boolean {
  if (error instanceof ApiError) return error.reasonCode === NO_WEBAUTHN;
  return error instanceof DOMException && (error.name === "NotSupportedError" || error.name === "SecurityError");
}

/** `create()` found a passkey the bank already knows on this phone (its excludeCredentials). */
export function isKnownHere(error: unknown): boolean {
  return error instanceof DOMException && error.name === "InvalidStateError";
}

/** The OS sheet closing is not an error the customer can act on; it is a state. */
export function isCancellation(error: unknown): boolean {
  if (error instanceof ApiError) return error.reasonCode === SHEET_CANCELLED;
  if (error instanceof DOMException) {
    return error.name === "NotAllowedError" || error.name === "AbortError";
  }
  return false;
}
